import { useNavigate } from '@tanstack/react-router'
import { keepChannel } from '@/features/pools/lastPool'
import { useState } from 'react'
import { toast } from 'sonner'

import { useChannels } from '@/api/endpoints/channels'
import { useCreatePool } from '@/api/endpoints/pools'
import { Button } from '@/components/ui/Button'
import { DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Segmented } from '@/components/ui/Segmented'
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValue } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { errorMessage } from '@/lib/errors'
import styles from '@/features/pools/pages/PoolsPage/PoolsPage.module.scss'

type Language = 'pt-br' | 'en'
// O Radix Select não aceita valor vazio.
const NO_SOURCE = '__none'

/** Criar um pool: nome, idioma e, opcionalmente, a geração de um canal como ponto de partida. */
export function CreatePoolDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const { data: channels = [] } = useChannels()
  const create = useCreatePool()
  const [name, setName] = useState('')
  const [language, setLanguage] = useState<Language>('pt-br')
  const [source, setSource] = useState(NO_SOURCE)

  const submit = () => {
    create.mutate(
      { name: name.trim(), language, from_channel: source === NO_SOURCE ? null : source },
      {
        onSuccess: (pool) => {
          toast.success(`Pool ${pool.name} criado`)
          onClose()
          setName('')
          setSource(NO_SOURCE)
          void navigate({ to: '/pools/$slug', params: { slug: pool.slug }, search: keepChannel })
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Não foi possível criar o pool'))
        },
      },
    )
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        className={styles.formDialog}
        title="Novo pool"
        description="Um acervo de vídeos sem marca. Os canais ligados a ele publicam o que ele gera."
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              disabled={!name.trim() || create.isPending}
              leadingIcon={create.isPending ? <Spinner /> : undefined}
              onClick={submit}
            >
              Criar pool
            </Button>
          </>
        }
      >
        <div className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="pool-create-name">Nome</label>
            <Input
              id="pool-create-name"
              value={name}
              placeholder="Curiosidades PT"
              maxLength={80}
              onChange={(e) => {
                setName(e.target.value)
              }}
            />
          </div>
          <div className={styles.field}>
            <span>Idioma</span>
            <Segmented<Language>
              aria-label="Idioma do pool"
              size="sm"
              value={language}
              onValueChange={setLanguage}
              options={[
                { value: 'pt-br', label: 'PT-BR' },
                { value: 'en', label: 'EN' },
              ]}
            />
            <small>Um idioma por pool. Os canais ligados publicam nesse idioma.</small>
          </div>
          <div className={styles.field}>
            <label htmlFor="pool-create-source">Começar com a geração de um canal</label>
            <SelectRoot value={source} onValueChange={setSource}>
              <SelectTrigger id="pool-create-source" aria-label="Canal de origem da configuração">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SOURCE}>Não, começar do zero</SelectItem>
                {channels.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
            <small>Copia factory, agenda e voz. A marca não vem: vídeo de pool sai sem marca.</small>
          </div>
        </div>
      </DialogContent>
    </DialogRoot>
  )
}
